/**
 * Helpdesk Kanban View
 *
 * Uses the global Kanban component with helpdesk-specific configuration.
 * All column definitions come from helpdeskPhaseConfig.ts
 */

import { useState, useEffect, memo } from 'react';
import { Plus } from "@phosphor-icons/react";
import { HelpdeskTicketInterface, TicketInternalStatus } from "shared/types/helpdesk";
import { HelpdeskCard } from './HelpdeskCard';
import { Kanban } from 'shared/components/Kanban';
import { helpdeskKanbanConfig } from 'shared/components/Kanban/configs/helpdeskKanbanConfig';
import { getStatusLabel, mapLegacyStatus } from '../helpdeskPhaseConfig';
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";

interface HelpdeskKanbanViewProps {
  tickets: HelpdeskTicketInterface[];
  onUpdate: () => void;
  onUpdateTicket: (ticketId: number, updates: any) => void;
  onTicketClick: (ticket: HelpdeskTicketInterface) => void;
  onQuickAdd?: () => void;
  onColumnHeaderClick?: (status: TicketInternalStatus) => void;
  onFetchAllInColumn?: (status: TicketInternalStatus) => void;
  // Optional pagination config for all columns
  columnPagination?: Array<{
    columnId: any;
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    loadMoreText?: string;
    loadingText?: string;
  }>;
  // Column visibility control
  hiddenColumns?: string[];
  onToggleColumnVisibility?: (columnId: string) => void;
  // Font size control
  fontSize?: number;
  onIncreaseFontSize?: () => void;
  onDecreaseFontSize?: () => void;
}

const HelpdeskKanbanViewComponent = ({
  tickets,
  onUpdate,
  onUpdateTicket,
  onTicketClick,
  onQuickAdd,
  onColumnHeaderClick,
  onFetchAllInColumn,
  columnPagination,
  hiddenColumns,
  onToggleColumnVisibility,
  fontSize = 12,
  onIncreaseFontSize,
  onDecreaseFontSize
}: HelpdeskKanbanViewProps) => {
  const { session } = useSession();
  
  // Local state for optimistic updates
  // Filter out tickets without IDs to satisfy KanbanItem constraint
  // Apply legacy status mapping for backward compatibility
  const [localTickets, setLocalTickets] = useState(
    tickets
      .filter(t => t.id !== undefined)
      .map(t => ({
        ...t,
        internalStatus: mapLegacyStatus(t.internalStatus as any)
      }))
  );

  // Update local state when prop changes (but only if it's actually different)
  // This prevents resetting optimistic updates
  useEffect(() => {
    // Filter tickets with defined IDs and map legacy statuses
    const filteredTickets = tickets
      .filter(t => t.id !== undefined)
      .map(t => ({
        ...t,
        internalStatus: mapLegacyStatus(t.internalStatus as any)
      }));

    // Only update if the data has actually changed (e.g., after a full refresh)
    // We check by comparing the stringified version to avoid unnecessary resets
    const currentIds = localTickets.map(t => `${t.id}-${t.internalStatus}`).sort().join(',');
    const newIds = filteredTickets.map(t => `${t.id}-${t.internalStatus}`).sort().join(',');

    if (currentIds !== newIds) {
      setLocalTickets(filteredTickets);
    }
  }, [tickets, localTickets]);

  /**
   * Handle drop from the Kanban component
   */
  const handleDrop = async (ticketId: string | number, newStatus: TicketInternalStatus, oldStatus: TicketInternalStatus) => {
    // OPTIMISTIC UPDATE: Update both local and parent state immediately
    setLocalTickets((prev) =>
      prev.map(t =>
        t.id === ticketId
          ? { ...t, internalStatus: newStatus }
          : t
      )
    );

    // Also update parent state for immediate UI response
    onUpdateTicket(Number(ticketId), { internalStatus: newStatus });

    // Make API call in background
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticketId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ internalStatus: newStatus }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to update ticket status");
      }

      toast.success(`Ticket H${ticketId} verplaatst naar "${getStatusLabel(newStatus)}"`);
    } catch (error: any) {
      console.error("Error updating ticket status:", error);
      toast.error(`Fout bij bijwerken status: ${error.message || 'Onbekende fout'}`);

      // ROLLBACK: Revert both local and parent state on error
      setLocalTickets((prev) =>
        prev.map(t =>
          t.id === ticketId
            ? { ...t, internalStatus: oldStatus }
            : t
        )
      );

      // Revert parent state
      onUpdateTicket(Number(ticketId), { internalStatus: oldStatus });

      // Refetch to ensure data consistency
      onUpdate();
    }
  };

  /**
   * Render a helpdesk ticket card
   */
  const renderTicketCard = (ticket: HelpdeskTicketInterface) => {
    return (
        <HelpdeskCard
          ticket={ticket}
          onClick={() => onTicketClick(ticket)}
          fontSize={fontSize}
        />
    );
  };

  const kanbanComponent = (
    <Kanban
      config={helpdeskKanbanConfig as any}
      items={localTickets as any}
      renderCard={renderTicketCard as any}
      onDrop={handleDrop}
      onCardClick={onTicketClick as any}
      onColumnHeaderClick={onColumnHeaderClick as any}
      onFetchAllInColumn={onFetchAllInColumn as any}
      columnPagination={columnPagination as any}
      hiddenColumns={hiddenColumns as any}
      onToggleColumnVisibility={onToggleColumnVisibility as any}
      fontSize={fontSize}
      onIncreaseFontSize={onIncreaseFontSize}
      onDecreaseFontSize={onDecreaseFontSize}
    />
  );

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-0">
        {/* Quick Add Button */}
        {onQuickAdd && (
          <div className="mb-4 flex justify-end">
            <Button
              onClick={onQuickAdd}
              className="bg-moss-primary hover:bg-moss-primary/90"
            >
              <Plus className="h-5 w-5 mr-2" weight="bold" />
              Ticket toevoegen
            </Button>
          </div>
        )}
        
        {kanbanComponent}
      </div>
    </div>
  );
};

// PERFORMANCE: Memoize to prevent re-renders when parent re-renders with same props
export const HelpdeskKanbanView = memo(HelpdeskKanbanViewComponent);

